import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { NotificationComponent, NotificationLayoutSimpleComponent } from '@joii/design-system';
import { filter, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { isMaintenanceWindowUrl, MAINTENANCE_WINDOW_PATH } from 'src/shared/constants/maintenance-window.constants';
import { MaintenanceWindowService } from 'src/shared/services/maintenance-window/maintenance-window.service';

const DISMISSED_STORAGE_KEY = 'maintenance-window-dismissed-ids';
const LOGIN_PATH = 'login';

/**
 * Mounted once in every app's root component. Shows a dismissible banner ahead of an
 * upcoming maintenance window, and redirects to a blank page for the duration of an active one.
 */
@Component({
  selector: 'app-maintenance-window',
  templateUrl: './maintenance-window.component.html',
  imports: [DatePipe, NotificationComponent, NotificationLayoutSimpleComponent],
})
export class MaintenanceWindowComponent {
  private readonly maintenanceWindowService = inject(MaintenanceWindowService);
  private readonly router = inject(Router);
  private readonly site = environment.app;

  private readonly upcomingWindows = toSignal(this.maintenanceWindowService.getUpcomingWindows(this.site), {
    initialValue: [],
  });
  // No `initialValue` deliberately: `undefined` means "haven't heard from Firestore yet" and must
  // not be treated the same as `null` ("loaded, and confirmed nothing is active"), or a page
  // refresh on the maintenance page would bounce straight to login before the real data arrives.
  private readonly currentWindow = toSignal(this.maintenanceWindowService.getCurrentWindow(this.site));
  // Tracked as a signal (not read ad hoc from `router.url`) so the effect below also re-evaluates
  // on direct navigation — e.g. someone typing /maintenance-window into the address bar while
  // nothing is active, or navigating away mid-outage — not just when Firestore data changes.
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );
  private readonly dismissedIds = signal<ReadonlySet<string>>(this.readDismissedIds());

  /** Every upcoming window not yet dismissed, one banner each. */
  readonly visibleWindows = computed(() =>
    this.upcomingWindows().filter((window) => !this.dismissedIds().has(window.id))
  );

  constructor() {
    effect(() => {
      const current = this.currentWindow();
      const onMaintenancePage = isMaintenanceWindowUrl(this.currentUrl());
      if (current === undefined) {
        return; // Still waiting on the first Firestore snapshot.
      }

      if (current) {
        if (!onMaintenancePage) {
          this.router.navigateByUrl(`/${MAINTENANCE_WINDOW_PATH}`);
        }
        return;
      }

      if (onMaintenancePage) {
        this.router.navigateByUrl(`/${LOGIN_PATH}`);
      }
    });
  }

  dismiss(windowId: string): void {
    const next = new Set(this.dismissedIds());
    next.add(windowId);
    this.dismissedIds.set(next);
    this.persistDismissedIds(next);
  }

  private readDismissedIds(): ReadonlySet<string> {
    if (typeof localStorage === 'undefined') return new Set();
    try {
      const stored = localStorage.getItem(DISMISSED_STORAGE_KEY);
      const parsed: unknown = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? new Set(parsed.filter((id): id is string => typeof id === 'string')) : new Set();
    } catch {
      return new Set();
    }
  }

  private persistDismissedIds(ids: ReadonlySet<string>): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...ids]));
    } catch {
      // Storage disabled, full, or blocked — dismissal won't persist across reloads.
    }
  }
}
